/**
 * Where what was said on a call is kept (D-225): a note on the call itself — read there, and on
 * the timeline of what the call is about (an opportunity's, a lead's, an account's), which
 * shows the notes of its activities with the activity they are on.
 */
export function callNoteTarget(activity) {
  return { entity: 'activity', uuid: activity.uuid }
}

/** Plain text as a note: paragraphs at blank lines, line breaks kept. */
export function textDoc(text) {
  return {
    type: 'doc',
    content: String(text).split(/\n{2,}/).map((p) => ({
      type: 'paragraph',
      content: p.split('\n').flatMap((line, i) => [...(i ? [{ type: 'hardBreak' }] : []), ...(line ? [{ type: 'text', text: line }] : [])]),
    })),
  }
}

/**
 * The record a number for the call goes to: the person it is with (a contact or a lead), else
 * what it is about when that can hold a number (an account, a contact, a lead); null when
 * nothing can (an opportunity without a contact).
 */
export function phoneTarget(activity, person = null) {
  if (person?.uuid && ['contact', 'lead', 'account'].includes(person.entity)) return { entity: person.entity, uuid: person.uuid, label: person.label }
  const about = activity.regarding
  const entity = activity.regarding_type || about?.entity
  if (about?.uuid && ['account', 'contact', 'lead'].includes(entity)) return { entity, uuid: about.uuid, label: about.label }
  return null
}

/**
 * Add a number to the record a call's number goes to (`phoneTarget`): a lead's phone; a
 * contact's or an account's numbers as a set, the new one beside the others (the first of its
 * kind primary). Returns what saving it returned (saved, queued offline, or why not).
 */
export async function addPhone(target, value, kind = 'work') {
  const { getRecord } = await import('@/lib/records.js')
  // What the company's rules require of the record, asked for with the number (D-343).
  const { saveFilling } = await import('@/lib/saveFilling.js')
  const record = (await getRecord(target.entity, target.uuid)).data
  if (target.entity === 'lead') return saveFilling('lead', target.uuid, { phone: value }, { record, baseVersion: record.version, label: record.label })
  const points = (record.contact_points || []).map((p) => ({ uuid: p.uuid, channel: p.channel, kind: p.kind, label: p.label ?? null, value: p.value, is_primary: !!p.is_primary }))
  const first = !points.some((p) => p.channel === 'phone')
  return saveFilling(target.entity, target.uuid, { contact_points: [...points, { channel: 'phone', kind, value, is_primary: first }] }, { record, baseVersion: record.version, label: record.label })
}

/** A number as people type it: digits, spaces, dashes, brackets, a leading plus — at least six. */
export const looksLikePhone = (value) => /^\+?[\d\s\-()]{6,}$/.test(String(value || '').trim())
